import React, { useEffect, useState } from 'react'
import { CellLoadingProps, CellSuccessProps, useQuery, useMutation, CellFailureProps } from '@redwoodjs/web'
import { AltiumLogo } from 'src/components/CadLogo'
import AltiumLogin from './AltiumLogin'

import type {
  CheckNexarTokenQuery,
  AltiumBomQuery,
  AltiumBomQueryVariables,
  AltiumInfoQuery,
  AltiumInfoQueryVariables
} from 'types/graphql'
import * as ListBox from '../ListBox'
import { BomRow } from 'src/lib/mapping/1-fileToRows'
import { reportGenericError } from 'src/lib/reportError'
import { prettyDateTime } from 'src/lib/formatters'
import { LoadingSpinnerWithDelay } from '../Loading'


export const ALTIUM_INFO_QUERY = gql`
  query AltiumInfoQuery ($input: AltiumInfoInput!) {
    altiumInfo (input: $input) {
      loading
      data {
        url
        name
        isDefault
        projects {
          id
          name
          design {
            releases {
              nodes {
                id
                releaseId
                createdAt
                description
              }
            }
          }
        }
      }
    }
  }
`

export const QUERY = gql`
  query CheckNexarTokenQuery {
    nexarToken {
      id
      expiresAt
    }
  }
`

export const ALTIUM_BOM_QUERY = gql`
  query AltiumBomQuery($input: AltiumBomInput!) {
    altiumBom(input: $input) {
      status
      data
    }
  }
`

type Props = {
  onBomChange: (rows: BomRow[] | null) => void
  onLoading: () => void
  orgId: string
  userId: number
}

type SuccessProps = CellSuccessProps<CheckNexarTokenQuery> & Props
type LoadingProps = CellLoadingProps<CheckNexarTokenQuery> & Props
type FailureProps = CellFailureProps<CheckNexarTokenQuery> & Props

export const Loading = (props: LoadingProps) => {
  return <AltiumImport {...props} cellLoading />
}

export const Failure = (props: FailureProps) => {
  const eventId = reportGenericError(props.error)
  return <div className='bg-red-200 p-2'>
    <div className='font-medium'>
      There was an error connecting with Altium
    </div>
    <div className='text-sm'>Contact Bomello support with error ID: <span className='font-bold'>{eventId}</span> to escalate a bug fix.</div>
  </div>
}

export const Success = (props: SuccessProps) => {
  return <AltiumImport {...props} />
}

type AltiumImportProps = Partial<CellSuccessProps<CheckNexarTokenQuery>> & Props & { cellLoading?: boolean }
const AltiumImport = ({ nexarToken, onBomChange, onLoading, orgId, userId, cellLoading }: AltiumImportProps) => {
  const [loading, setLoading] = useState(false)
  const [infoStatusId] = useState(String(Math.random()))
  const [documentId, setDocumentId] = useState<null | string>(null)
  const [versionId, setVersionId] = useState<null | string>(null)
  const [selectedWorkspace, setSelectedWorkspace] = useState<string | null>(null)
  const [altiumInfo, setAltiumInfo] = useState<AltiumInfoQuery['altiumInfo'] | null>(null)

  // Query to get Altium information
  const {
    refetch: fetchAltiumInfo,
    loading: altiumInfoLoading
  } = useQuery<AltiumInfoQuery, AltiumInfoQueryVariables>(ALTIUM_INFO_QUERY, {
    skip: !nexarToken?.id,
    variables: {
      input: {
        statusId: infoStatusId
      }
    },
    onCompleted: (data) => {
      setAltiumInfo(data.altiumInfo)

      // Set default workspace if loading is complete
      if (!data.altiumInfo.loading && data.altiumInfo.data) {
        const defaultWorkspace = data.altiumInfo.data.find(w => w.isDefault)
        if (defaultWorkspace && !selectedWorkspace) {
          setSelectedWorkspace(defaultWorkspace.url)
        }
      }
    }
  })

  // Poll altiumInfo until loading is false
  useEffect(() => {
    let pollingInterval: NodeJS.Timeout | null = null;

    if (nexarToken?.id && altiumInfo?.loading) {
      pollingInterval = setInterval(() => {
        fetchAltiumInfo()
      }, 5000)
    }

    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval)
      }
    }
  }, [nexarToken, altiumInfo, fetchAltiumInfo])

  // Define a proper interface for the selected project
  interface AltiumProject {
    id: string;
    name: string;
    partNumber?: string;
    releases?: {
      id: string;
      releaseId: string;
      createdAt: string;
      description: string;
    }[];
  }

  // Get selected workspace data
  const selectedWorkspaceData = selectedWorkspace && altiumInfo?.data
    ? altiumInfo.data.find(w => w.url === selectedWorkspace)
    : null

  // Display functions
  const displayValue = (id: string | null) => {
    if (!id || !selectedWorkspaceData) return 'Select Altium Project'
    const project = selectedWorkspaceData.projects?.find(d => d.id === id)
    return project ? project.name : 'Select Altium Project'
  }

  const displayWorkspaceValue = (url: string | null) => {
    if (!url || !altiumInfo) return 'Select Workspace'
    const workspace = altiumInfo.data?.find(w => w.url === url)
    return workspace ? workspace.name : 'Select Workspace'
  }

  const onChange = (id: string) => {
    setDocumentId(id)
    setVersionId(null)
    onBomChange(null)
  }

  const onWorkspaceChange = (url: string) => {
    setSelectedWorkspace(url)
    setDocumentId(null)
    setVersionId(null)
    onBomChange(null)
  }

  const selectedProject = selectedWorkspaceData?.projects.find(d => d.id === documentId)
  const releases = selectedProject?.design.releases.nodes

  // BOM fetching logic
  const { refetch: fetchAltiumBom } = useQuery<AltiumBomQuery, AltiumBomQueryVariables>(
    ALTIUM_BOM_QUERY,
    { skip: true }
  )

  useEffect(() => {
    let pollInterval: NodeJS.Timeout | null = null;
    let attempts = 0;
    const MAX_ATTEMPTS = 60; // 5 minutes worth of 5-second intervals

    const getBom = async (documentId: string, versionId: string) => {
      attempts++;
      if (attempts >= MAX_ATTEMPTS) {
        if (pollInterval) {
          clearInterval(pollInterval)
          pollInterval = null
        }
        onBomChange(null) // or handle timeout however you prefer
        return
      }

      onLoading()
      try {
        // Make sure selectedProject exists and has required properties
        if (!selectedProject || !selectedProject.id) {
          console.error('Invalid project data')
          return
        }

        const { data: { altiumBom } } = await fetchAltiumBom({
          input: {
            id: versionId,
            projectId: selectedProject.id
          }
        })

        switch (altiumBom.status) {
          case 'complete':
            if (altiumBom.data) {
              const withMetadata = (altiumBom.data as any[]).map(r => {
                return {
                  ...r,
                  __metadata: {
                    originalIndex: -1,
                    messages: []
                  }
                } as BomRow
              })
              onBomChange(withMetadata)
              if (pollInterval) {
                clearInterval(pollInterval)
                pollInterval = null
              }
            }
            break;

          case 'error':
            // console.error('Error fetching BOM:', altiumBom.error)
            // Could add specific error handling here
            break;

          case 'pending':
            break;
        }
      } catch (error) {
        console.error('Failed to fetch BOM:', error)
      }
    }

    if (documentId && versionId) {
      attempts = 0 // Reset attempts counter
      getBom(documentId, versionId)

      pollInterval = setInterval(() => {
        if (documentId && versionId) {
          getBom(documentId, versionId)
        }
      }, 5000)
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval)
      }
    }
  }, [documentId, versionId])

  // Check if any loading state is active
  const isLoading = cellLoading || altiumInfoLoading || altiumInfo?.loading;

  if (!cellLoading && !nexarToken?.id) {
    return <AltiumLogin className="p-4 gap-4" />
  }

  if (isLoading) {
    return <LoadingSpinnerWithDelay />
  }
  return (
    <div className='flex flex-col gap-4 items-end'>
      <ListBox.ListBox onChange={onWorkspaceChange} value={selectedWorkspace}>
        {({ open }) =>
          <div className="relative h-full">
            <ListBox.Button
              className='py-2'
              displayValue={displayWorkspaceValue(selectedWorkspace)}
              disabled={cellLoading || altiumInfoLoading || altiumInfo?.loading}
            />
            <ListBox.Options open={open} align='right' className='text-sm'>
              {altiumInfo?.data ? altiumInfo.data.map(w => (
                <ListBox.Option
                  key={w.url}
                  className='py-3'
                  value={w.url}
                  display={w.name}
                />
              )) : null}
              {altiumInfo?.data && altiumInfo.data.length === 0 && <div className='py-3 px-4 text-gray-600 italic'>No Workspaces Found</div>}
            </ListBox.Options>
          </div>
        }
      </ListBox.ListBox>

      <ListBox.ListBox onChange={onChange} value={documentId}>
        {({ open }) =>
          <div className="relative h-full">
            <ListBox.Button
              className='py-2'
              displayValue={displayValue(documentId)}
              disabled={cellLoading || altiumInfo?.loading || !selectedWorkspaceData}
            />
            <ListBox.Options open={open} align='right' className='text-sm'>
              {selectedWorkspaceData?.projects ? selectedWorkspaceData.projects.map(d => (
                <ListBox.Option
                  key={d.id}
                  className='py-3'
                  value={d.id}
                  display={d.name}
                />
              )) : null}
              {selectedWorkspaceData?.projects && selectedWorkspaceData.projects.length === 0 &&
                <div className='py-3 px-4 text-gray-600 italic'>No Designs Found</div>
              }
            </ListBox.Options>
          </div>
        }
      </ListBox.ListBox>
      {
        <VersionInput
          releases={releases}
          versionId={versionId}
          setVersionId={setVersionId}
        />
      }
    </div>
  )
}

// VersionInput component remains the same
type AltiumRelease = {
  id: string
  releaseId: string
  description: string
  createdAt: string
}

type VersionInputProps = {
  releases?: AltiumRelease[]
  setVersionId: (v: string) => void
  versionId: string | null
}

export const VersionInput = ({ releases, setVersionId, versionId }: VersionInputProps) => {
  const displayValue = (id: string | null, verbose = true) => {
    if (!id || !releases) return 'Select Project Release'
    const r = releases.find(d => d.id === id)!
    if (!verbose) return r.releaseId
    return <div className='flex gap-1 flex-col'>
      <div className='flex gap-2'>
        <div className='font-medium'>ID</div>
        <div>{r.releaseId}</div>
      </div>
      <div className='flex gap-2'>
        <div className='font-medium'>@</div>
        <div>{prettyDateTime(new Date(r.createdAt))}</div>
      </div>
      <div className='italic'>{r.description}</div>
    </div>
  }

  const onChange = (id: string) => {
    setVersionId(id)
  }

  return (
    <ListBox.ListBox onChange={onChange} value={versionId} disabled={!releases}>
      {({ open }) =>
        <div className="relative h-full">
          <ListBox.Button
            className='py-2'
            displayValue={displayValue(versionId, false)}
            disabled={!releases}
          />
          <ListBox.Options open={open} align='right' className='text-sm'>
            {(releases || []).map(d => (
              <ListBox.Option
                key={d.id}
                className='py-3'
                value={d.id}
                display={displayValue(d.id)}
              />
            ))}
            {releases?.length === 0 && <div className='py-3 px-4 text-gray-600 italic'>No Releases Found</div>}
          </ListBox.Options>
        </div>
      }
    </ListBox.ListBox>
  )
}
